import React from 'react'
import {
  Form,
  Input,
  Select,
  Button,
  Card,
  Typography,
  Space,
  App,
  Tooltip,
  Alert
} from 'antd'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, HelpCircle } from 'lucide-react'
import { useCompanyStore } from '../../../store/company/companyStore'
import { useApi } from '../../../services/ApiContext'
import { TeamMember } from '../../../types/ffe'
import { ApiError } from '../../../services/types'

const { Title, Text } = Typography

const roleDescriptions = {
  administrator: {
    title: 'Administrator',
    description: 'Full system access including:',
    permissions: [
      'Manage all documents and projects',
      'Manage team members and roles',
      'Configure system settings',
      'Manage integrations',
      'Access billing and subscription settings',
      'View audit logs and activity reports'
    ]
  },
  designer: {
    title: 'Designer',
    description: 'Standard user access including:',
    permissions: [
      'Manage all documents and projects',
      'Export documents',
      'Access shared team resources'
    ]
  }
}

export const UserPage: React.FC = () => {
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const { id } = useParams()
  const { message, modal } = App.useApp()
  const user = useCompanyStore((state) =>
    state.teamMembers.find((u) => u._id === id)
  )
  const updateTeamMember = useCompanyStore((state) => state.updateTeamMember)
  const updateUser = useCompanyStore((state) => state.updateUser)
  const self = useCompanyStore((state) => state.user)
  const api = useApi()

  React.useEffect(() => {
    if (user) {
      form.setFieldsValue(user)
    }
  }, [user, form])

  const editingSelf = self?._id === id

  const handleSubmit = async (values: TeamMember) => {
    if (!id || !self) return
    try {
      if (editingSelf) {
        const newTeamMember = await api.patchTeamMember(id, {
          email: values.email,
          firstName: values.firstName,
          lastName: values.lastName
        })
        updateTeamMember(newTeamMember)
        updateUser({
          ...self,
          ...newTeamMember
        })
      } else {
        const newTeamMember = await api.patchTeamMember(id, {
          email: values.email,
          firstName: values.firstName,
          lastName: values.lastName,
          roles: values.roles
        })
        updateTeamMember(newTeamMember)
      }
      message.success(`Team member details updated`)
    } catch (error) {
      if (error instanceof ApiError) {
        message.error(error.message)
      } else {
        message.error('An error occurred')
      }
    }
  }

  // const handleResetPassword = () => {
  //   modal.confirm({
  //     title: 'Reset Password',
  //     content:
  //       "Are you sure you want to reset this user's password? They will receive an email with instructions to set a new password.",
  //     onOk: () => {
  //       message.success('Password reset email sent')
  //     }
  //   })
  // }

  const handleToggleStatus = () => {
    modal.confirm({
      title: `${user?.isDisabled ? 'Enable' : 'Disable'} User`,
      content: `Are you sure you want to ${user?.isDisabled ? 'enable' : 'disable'} this user? ${
        !user?.isDisabled
          ? 'They will no longer be able to access the system.'
          : ''
      }`,
      onOk: async () => {
        if (!id) return
        const newTeamMember = await api.patchTeamMember(id, {
          isDisabled: !user?.isDisabled
        })
        updateTeamMember(newTeamMember)
        message.success(
          `Team member ${user?.isDisabled ? 'enabled' : 'disabled'} successfully`
        )
      }
    })
  }

  const renderRoleTooltip = (role: keyof typeof roleDescriptions) => (
    <div className="max-w-xs">
      <Text strong className="block mb-2 text-white">
        {roleDescriptions[role].description}
      </Text>
      <ul className="list-disc pl-4 space-y-1">
        {roleDescriptions[role].permissions.map((permission, index) => (
          <li key={index} className="text-sm">
            {permission}
          </li>
        ))}
      </ul>
    </div>
  )

  if (!user) {
    navigate('/settings/users')
    return null
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col items-start gap-2">
        <Button
          type="text"
          icon={<ArrowLeft className="h-4 w-4" />}
          onClick={() => navigate('/app/settings/users')}
        >
          Back to Team Members
        </Button>
        <div>
          <Title level={4} className="!mb-0">
            {user.firstName} {user.lastName}
          </Title>
          <Text type="secondary">Team Member</Text>
        </div>
      </div>

      {user.isDisabled && (
        <Alert
          className="mb-4"
          message="This user has been disabled, they will no longer be able to access their account."
          type="warning"
          showIcon
        />
      )}
      <Card>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <div className="flex gap-4 w-full">
            <Form.Item
              className="flex-1"
              name="firstName"
              label="First Name"
              rules={[
                {
                  required: true,
                  message: "Please enter this team member's first name"
                }
              ]}
            >
              <Input placeholder="Enter team member's first name" />
            </Form.Item>

            <Form.Item
              className="flex-1"
              name="lastName"
              label="Last Name"
              rules={[
                {
                  required: true,
                  message: "Please enter this team member's last name"
                }
              ]}
            >
              <Input placeholder="Enter team member's last name" />
            </Form.Item>
          </div>
          <Form.Item
            name="email"
            label="Email Address"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Please enter a valid email' }
            ]}
          >
            <Input placeholder="Enter team member's email address" />
          </Form.Item>

          <Form.Item
            className="max-w-[400px]"
            name="roles"
            label={
              <Space>
                Role
                <Tooltip
                  title="Click on a role to see detailed permissions"
                  placement="right"
                >
                  <HelpCircle className="h-4 w-4 text-gray-400" />
                </Tooltip>
              </Space>
            }
            rules={[{ required: true, message: 'Please select a role' }]}
          >
            <Select
              disabled={editingSelf}
              value={user.roles[0]}
              onChange={(value) => form.setFieldsValue({ roles: [value] })}
              options={[
                {
                  label: (
                    <Tooltip
                      title={renderRoleTooltip('administrator')}
                      placement="right"
                      overlayClassName="w-80"
                    >
                      <div>Administrator</div>
                    </Tooltip>
                  ),
                  value: 'orgManager'
                },
                {
                  label: (
                    <Tooltip
                      title={renderRoleTooltip('designer')}
                      placement="right"
                      overlayClassName="w-80"
                    >
                      <div>Designer</div>
                    </Tooltip>
                  ),
                  value: 'basic'
                }
              ]}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Save Changes
              </Button>
              <>
                {/* <Button
                  icon={<Key className="h-4 w-4" />}
                  onClick={handleResetPassword}
                >
                  Reset Password
                </Button> */}
                <Button
                  disabled={editingSelf}
                  danger={!user.isDisabled}
                  onClick={handleToggleStatus}
                >
                  {!user.isDisabled ? 'Disable Account' : 'Enable Account'}
                </Button>
              </>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  )
}
